import React, { useState } from 'react';
import SVGTransformer from '../../../../../lib/SVGTransformer';
import { fetchImage, LAYER_TYPES } from '../../../../../lib';

const imageResourcesCached = {};
const imageResourcesLoading = {};
const imageResourcesLoadTimes = {};

const isImageLoaded = (id) => !!imageResourcesCached[id];
const isImageLoading = (id) => !!imageResourcesLoading[id];

const loadPreviewImage = async({ id, previewUrl }) => {
    if (isImageLoaded(id)) {
        return imageResourcesLoadTimes[id];
    }
    if (isImageLoading(id)) {
        await imageResourcesLoading[id];
        return imageResourcesLoadTimes[id];
    }

    const startTime = Date.now();
    const promise = fetchImage({ src: previewUrl });
    imageResourcesLoading[id] = promise;
    await promise;
    const loadTime = Date.now() - startTime;
    imageResourcesLoadTimes[id] = loadTime;
    imageResourcesCached[id] = true;
    imageResourcesLoading[id] = null;
    return loadTime;
};

const getVariationFontUrls = (variation) => {
    const textRoots = variation.roots.filter((root) => root.layerType === LAYER_TYPES.TEXT);

    return textRoots.map((root) => root.fontUrl);
};

const loadVariationFontTransformers = (variation) => {
    const variationFontUrls = getVariationFontUrls(variation);

    return Promise.all(variationFontUrls.map(SVGTransformer.loadTransformer));
};

const isVariationReady = (variation, isPreview) => {
    const variationFontUrls = getVariationFontUrls(variation);

    return variationFontUrls.every(SVGTransformer.isTransformerActive) && (
        !isPreview || isImageLoaded(variation)
    );
};

export const useVariationResourceLoad = (variation, isPreview) => {
    const [previewLoadingTime, setPreviewLoadingTime] = useState(null);
    const [isReady, setIsReady] = useState(isVariationReady(variation, isPreview));

    const loadVariation = () => {
        isPreview && loadPreviewImage(variation).then((loadTime) => {
            setPreviewLoadingTime(loadTime);

            // When image finishes, Is ready will be conditioned to the font resources.
            const variationFontUrls = getVariationFontUrls(variation);
            const isFontsTransformerReady = variationFontUrls.every(SVGTransformer.isTransformerActive);

            setIsReady(isFontsTransformerReady);
        });

        // Condition to text to path rendering in general.
        loadVariationFontTransformers(variation)
            .then(() => {
                const isImageReady = !isPreview || isImageLoaded(variation.id);

                setIsReady(isImageReady);
            })
            .catch(() => {
                setIsReady(false);
            });
    };

    return [loadVariation, isReady, previewLoadingTime];
};
